import { useInfiniteQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { ListParams } from '../../../api/listApi.types'
import { listQueryDataSignature } from './useListQuery'

/** Arguments passed to a cursor page fetcher. */
export interface CursorPageArgs {
  limit: number
  search?: string
  sort_by?: string
  order?: 'asc' | 'desc'
  filters?: Record<string, unknown>
  /** null on the first page. */
  cursor: string | null
}

/** One page returned by a cursor page fetcher. */
export interface CursorPage<T> {
  data: T[]
  nextCursor: string | null
  hasMore: boolean
}

export interface UseInfiniteListQueryOptions<T> {
  queryKey: unknown[]
  /** Row-defining params (filters/search/sort/limit). Page cursor is managed internally. */
  params: ListParams
  fetchPage: (args: CursorPageArgs) => Promise<CursorPage<T>>
  enabled?: boolean
}

export interface UseInfiniteListQueryResult<T> {
  /** All accumulated rows across fetched pages. */
  data: T[]
  isLoading: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: () => void
  error: Error | null
}

/**
 * Cursor-based sibling of useListQuery for infinite/"load more" feeds.
 *
 * The query key includes only the row-defining signature (not the cursor), so
 * changing filters/search/sort resets the accumulated list to page one, while
 * paging deeper simply appends.
 */
export function useInfiniteListQuery<T>({
  queryKey,
  params,
  fetchPage,
  enabled = true,
}: UseInfiniteListQueryOptions<T>): UseInfiniteListQueryResult<T> {
  const signature = listQueryDataSignature(params)

  const query = useInfiniteQuery({
    queryKey: [...queryKey, 'infinite', signature],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchPage({
        limit: params.limit,
        search: params.search,
        sort_by: params.sort_by,
        order: params.order,
        filters: params.filters,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
  })

  const data = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.data),
    [query.data],
  )

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: () => {
      void query.fetchNextPage()
    },
    error:
      query.error instanceof Error
        ? query.error
        : query.error
          ? new Error(String(query.error))
          : null,
  }
}
